import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { isNativePlatform, getNativePlatform } from '@lib/platform';
import { OUTLIVE_THE_SIGN_OUT_NAVIGATION } from '@api-client/requestConfig';
import {
  registerNativeDevice,
  unregisterNativeDevice,
  unsubscribeFromPush,
  type DeviceRegistrationRequest,
} from '@api-client/api-client';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useLatestRef } from '@hooks/useLatestRef';
import { useReportNotificationClick } from '@hooks/useReportNotificationClick';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { resolveNotificationTarget } from '@lib/notifications/resolveNotificationTarget';
import { extractRefs } from '@lib/pwa/push-navigation';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { isSafeInAppPath, NOTIFICATION_TAP_FALLBACK } from '@utils/safePath';

// FirebaseMessaging is dynamically imported to keep @capacitor-firebase/messaging
// out of web bundles (no sideEffects: false in package).
//
// AIDEV-NOTE: return the MODULE, never the plugin: a Capacitor plugin Proxy is a thenable that never settles.
//
// Every caller destructures:
// `const { FirebaseMessaging } = await loadFirebaseMessagingModule()`. A plugin is a
// Proxy whose `get` trap answers every unknown property, `then` included, with a native
// method wrapper (@capacitor/core `registerPlugin`), so the plugin is a thenable.
// Resolving a promise with it, by `.then(m => m.FirebaseMessaging)` or by
// `return FirebaseMessaging` from an async function, makes the promise call
// `FirebaseMessaging.then(resolve, reject)`; the wrapper rejects Unimplemented and calls
// neither, so the promise never settles. 66f00c798's message carries the #845 story.
// The test mocks carry the same `then`, both halves of it, so a plain-object mock cannot
// hide it again.
//
// ONE CACHED PROMISE, not one import SITE, is what makes this work under vitest. The
// hazard is overlapping imports, not the number of import expressions: a second import
// of a `vi.mock`ed module from this module that STARTS while another is still resolving
// gets the REAL module (the module runner keeps one call-stack array per importing
// module and takes its self-import branch), whatever the shape of the expression.
// Sequential imports of either shape both get the mock; concurrent callers of one cached
// promise all get the mock. The real plugin's jsdom web fallback reports permission
// 'denied' and silently aborts token setup, so moving any consumer onto an UNCACHED
// helper keeps the bug.
let firebaseMessagingModulePromise: Promise<typeof import('@capacitor-firebase/messaging')> | null =
  null;

const loadFirebaseMessagingModule = () => {
  // AIDEV-NOTE: the catch RESETS the cache; `??=` would otherwise keep a REJECTED import for the document's life.
  firebaseMessagingModulePromise ??= import('@capacitor-firebase/messaging').catch(
    (error: unknown) => {
      firebaseMessagingModulePromise = null;
      throw error;
    },
  );
  return firebaseMessagingModulePromise;
};

// ONE cached promise for Preferences too, for the same two reasons as FCM above.
// Under vitest the cache is what keeps every consumer on the MOCK: a second import
// of a `vi.mock`ed module that STARTS while another is still resolving takes the
// module runner's self-import branch and gets the REAL module, whatever the shape of
// the import expression. The sign-out teardown and `handleFcmToken` overlap in
// exactly that way, so an uncached helper per site is not equivalent.
let preferencesModulePromise: Promise<typeof import('@capacitor/preferences')> | null = null;

const loadPreferencesModule = () => {
  preferencesModulePromise ??= import('@capacitor/preferences').catch((error: unknown) => {
    preferencesModulePromise = null;
    throw error;
  });
  return preferencesModulePromise;
};

const TAP_DEDUPLICATION_WINDOW_MS = 2000;

/**
 * Where this device keeps the FCM token it last registered with the backend.
 * Written only once a registration succeeds; read on sign-out to name the
 * device in the backend DELETE.
 */
const PUSH_TOKEN_STORAGE_KEY = 'meetball_push_token';

/**
 * What this device last registered, and for WHOM.
 *
 * AIDEV-NOTE: the account is half the key (#1173); on the token alone, the NEXT account was deduped away.
 *
 * Comparing the token alone meant a different account signing in after a session that
 * ended without `unregisterPushToken` was never registered, so the backend kept the
 * token bound to the previous account.
 *
 * Two places remove this key, and no others (`storageCleanup.ts` deliberately leaves it:
 * "It must survive until unregisterPushToken() reads it during logout"): the sign-out
 * teardown in `unregisterPushToken`, which only `executeSignOut` runs, and `onSuccess`
 * when a registration settles for an account that is no longer the signed-in one, which
 * is where the record would otherwise go on claiming a binding the backend has already
 * given away. Every other path that ends a native session runs `clearAuthState` without
 * a teardown: `useInvalidGrantHandler`, `useSdkLoadingWatchdog`, and AuthProvider's
 * boot-cached-invalid and unexpected-loss branches. After any of those the FCM token is
 * still live and still stored, so the previous account's notifications kept arriving on
 * the new account's session.
 *
 * `userId: null` means a legacy bare-string value, which is read as "unknown account" so
 * the device registers once more and the backend's `upsert_device` reassigns the token.
 */
interface StoredPushRegistration {
  token: string;
  userId: string | null;
}

const parseStoredRegistration = (value: string | null): StoredPushRegistration | null => {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { token?: unknown }).token === 'string'
    ) {
      const { token, userId } = parsed as { token: string; userId?: unknown };
      return { token, userId: typeof userId === 'string' ? userId : null };
    }
  } catch {
    // Not JSON: the pre-#1173 format, a bare token string.
  }
  return { token: value, userId: null };
};

const serializeStoredRegistration = (record: StoredPushRegistration): string =>
  JSON.stringify(record);

/**
 * What `registerMutation` is given: the request, plus who it is for and which teardown
 * generation it was dispatched under. Both are read in `onSuccess`, which runs after
 * awaits the hook no longer controls.
 */
interface RegisterDeviceVariables {
  request: DeviceRegistrationRequest;
  userId: string | null;
  teardownGeneration: number;
}

/**
 * Bumped at the start of every native sign-out teardown (#1173). The
 * auth-gated token effect records the value it started under, and a token that
 * reaches `handleFcmToken` after a bump is not registered.
 *
 * `executeSignOut` runs `unregisterPushToken` BEFORE `clearAuthState` flips
 * `isAuthenticated`, so for that whole window the `tokenReceived` listener is
 * still attached and the session still valid. A token the SDK mints in it (a
 * refresh already scheduled or in flight, or a `handleFcmToken` from this run that is
 * already under way) would otherwise go to `registerNativeDevice`, and `upsert_device`
 * reassigns a token to whoever registers it: the signed-out device would be
 * bound straight back to the account that is leaving. A counter rather than a
 * flag, so nothing has to remember to clear it: the next authenticated run
 * simply starts under the new value, and a handler still in flight from the
 * old one can never match it again.
 *
 * What it does NOT cover, and what does: it only blocks runs that began BEFORE the
 * bump, so a new authenticated run started during the sign-out itself would register
 * freely. The sign-out latch in the auth store closes that, by keeping the current-user
 * query from handing the leaving user back. And a registration that has already reached
 * `mutate()` cannot be blocked at all, so `onSuccess` re-checks the generation it was
 * dispatched under before it writes the stored record.
 */
let nativePushTeardownGeneration = 0;

/**
 * Hook that manages native push notification lifecycle on Capacitor platforms.
 *
 * Uses @capacitor-firebase/messaging which returns FCM tokens directly
 * (unlike @capacitor/push-notifications which returns raw APNs tokens).
 *
 * Registers plugin event listeners:
 * - tokenReceived: FCM token received -> send to backend
 * - notificationActionPerformed: User tapped notification -> resolve the
 *   in-app destination and navigate (N1 destination contract, task W4;
 *   type-less payloads AND unresolvable typed payloads fall through to the
 *   legacy url/route ladder during the dual-emit transition)
 *
 * Foreground notifications are handled by the native system banner via
 * presentationOptions in capacitor.config.ts (no listener needed).
 *
 * No-op on web platforms -- web push is handled by the existing service worker.
 *
 * @param isAuthenticated - Only register for push when user is logged in
 * @param signedInUserId - Who the registration belongs to. Half the dedupe key, so a
 *   different account on this device is registered rather than skipped. `null` means
 *   "unknown account", which NEVER dedupes: measured cost is one redundant
 *   `register-device` per mount, which `upsert_device` absorbs. The sole caller cannot
 *   pass null while authenticated, because `useAuth().isAuthenticated` IS
 *   `isFullyAuthenticated`, which is built on `useCurrentUser`'s
 *   `isAuthenticated && !!user` -- the same `user` the context exposes. Cited BY SYMBOL:
 *   this claim carried line numbers into `AuthProvider` once and a rewrite of that file
 *   drifted them with no edit here. Failing open rather than skipping
 *   registration is deliberate: a missing id must not cost the device its push.
 */
export const usePushNotifications = (isAuthenticated: boolean, signedInUserId: string | null) => {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitudeTracking();
  const reportClick = useReportNotificationClick();
  // Refs for navigate/trackEvent/reportClick so the tap listener (registered
  // once) calls the latest value without re-subscribing. useLatestRef syncs in
  // an effect (not during render) to satisfy Rules of React / the React Compiler.
  const navigateRef = useLatestRef(navigate);
  const trackEventRef = useLatestRef(trackEvent);
  const reportClickRef = useLatestRef(reportClick);
  // Read inside the tap listener, which is registered once for the app's
  // lifetime and therefore cannot close over `isAuthenticated` directly.
  const isAuthenticatedRef = useLatestRef(isAuthenticated);
  // EVERY account read in `onSuccess` goes through this ref, including the
  // account-mismatch check that runs before any await. The closure is NOT reliably
  // fresh, and the reason is specific: `@tanstack/query-core` (5.101.4) refreshes a
  // mutation's options from `MutationObserver.setOptions` only while
  // `#currentMutation` is still pending (`mutationObserver.js`), and every `mutate()`
  // builds a new mutation and reassigns `#currentMutation`. So a POST that has been
  // SUPERSEDED by a later `mutate()` keeps the options, and therefore the `onSuccess`
  // closure, it had when it was current, however many renders have happened since.
  // Cited by behaviour rather than by line, which would drift on any bump.
  //
  // That is inter-mutation staleness, and it is a different thing from the
  // intra-callback staleness every await in this callback also opens. Both are real;
  // the ref covers both.
  const signedInUserIdRef = useLatestRef(signedInUserId);
  const lastHandledTapRef = useRef<{ key: string; at: number } | null>(null);
  // A cold-start tap is handled before the session finishes restoring, so the
  // N3 click report would 401 and be silently dropped. Park the id here and
  // flush it once auth lands (see the flush effect below).
  const pendingClickReportRef = useRef<string | null>(null);

  const registerMutation = useMutation<
    // Pinned, not inferred: `onSuccess` dispatches a corrective registration through
    // `registerMutation.mutate` (see the account check below), and a mutation referenced
    // inside its own initializer has no inferable type (TS7022).
    Awaited<ReturnType<typeof registerNativeDevice>>,
    Error,
    RegisterDeviceVariables
  >({
    // The request is NESTED rather than spread into the variables: react-query v5.101
    // calls mutationFn as (variables, context), and registerNativeDevice's second
    // parameter is an AxiosRequestConfig that gets spread into the request, so the bare
    // reference would splice react-query's MutationFunctionContext into the axios
    // config. Nesting also keeps the two bookkeeping fields out of the request body
    // without a strip step.
    mutationFn: (variables: RegisterDeviceVariables) => registerNativeDevice(variables.request),
    onSuccess: (_data, variables) => {
      // Persist the registration only after it succeeded, so a failed one retries on
      // the next app launch.
      void (async () => {
        // SYNCHRONOUSLY FIRST, before any await. A POST that was already sent (or
        // waiting in authAwareRetry's backoff) when sign-out began completes after the
        // teardown has removed the key, and this write would put the token back --
        // which then dedupes the NEXT account's registration away. The generation is
        // the one this mutation was dispatched under.
        if (variables.teardownGeneration !== nativePushTeardownGeneration) {
          logger.info('Push registration settled after sign-out began; not storing it');
          return;
        }
        // The generation only moves on an explicit sign-out, and the paths that end a
        // session without one (`useInvalidGrantHandler`, `useSdkLoadingWatchdog`,
        // AuthProvider's boot-cached-invalid and unexpected-loss branches) leave it
        // alone. So a POST for the LEAVING account can still settle after the next
        // account has signed in and registered, under the same generation, and this
        // write would put the previous account back into the record. Through the REF, so
        // this reads the account that is signed in NOW rather than the one this mutation
        // was dispatched for: a superseded POST's closure is frozen (see the
        // `signedInUserIdRef` comment), so a second same-account `mutate()` for the same
        // token would otherwise hide the switch entirely and this check would not fire.
        //
        // Dropping the write is only half of it (Copilot thread 4175744543). The POST
        // REACHED the backend, and `upsert_device` reassigns the token to whoever
        // registered last (backend `device_crud.upsert_device`), so the
        // token is now bound to the account that left while the record names the one
        // signed in. KEEPING that record makes it a lie nothing can correct: the dedupe
        // in the token effect matches token AND account, so every later token event for
        // this account returns early and the backend is never rebound. Before this check
        // existed the stale write landed, the dedupe failed, and the next token event
        // healed it -- the check removed exactly that healing. So forget the record,
        // which makes the dedupe fail open and GUARANTEES the correction, and re-register
        // straight away rather than waiting for a token event that may be days off.
        //
        // The two halves are not redundant: a re-registration that fails leaves the
        // record forgotten, so the next token event still rebinds. A sign-out in the gap
        // between them finds no stored token and sends no backend DELETE, which costs
        // nothing: that DELETE carries the signed-in account's bearer over a token the
        // backend has bound to the leaving one, and `deactivate_device_by_token` returns
        // None on exactly that mismatch, and `unregister_device` turns that into a 403.
        if (variables.userId !== signedInUserIdRef.current) {
          // ASK THE DEVICE WHAT TOKEN IT HOLDS, and ask FIRST (Copilot thread
          // 4177017060). Forget-and-rebind above reasons about the token NOT changing.
          // FCM rotates the device token around an account switch: the leaving account A
          // registered T1, FCM rotated T1 -> T2, the account signed in now (B) registered
          // T2, and only then does A's POST for T1 settle here. Rebinding the DEAD T1 for
          // B leaves the next sign-out DELETEing T1 while T2 stays bound to B on the
          // backend: the leak, inverted.
          //
          // The DEVICE'S LIVE TOKEN is the ground truth for that, and the stored record is
          // not. The record is a cache that MAY NOT EXIST YET: B's own first POST can
          // still be in flight when A's settles, and a record-only test then reads null,
          // concludes nothing has rotated, and rebinds the dead T1 anyway. That ordering
          // is the one a stored-record check cannot see, and it is the same failure.
          //
          // So: if the token this POST carried is no longer the live one, it is dead and
          // must not be re-registered under any account. Return without touching the
          // record. Whatever is there is either the valid `{T2, B}`, or absent and about
          // to be written by B's in-flight POST, or a stale entry naming a token that is
          // not live -- and a stale entry cannot wedge anything, because the dedupe in
          // `handleFcmToken` matches token AND account, so a record whose token is not the
          // live one never swallows the live token's event.
          //
          // `getToken()` can MINT a token (see the `unregisterNativePushToken` docblock),
          // and it is NOT ordered against the teardown: the `loadFirebaseMessagingModule()`
          // await below sits between it and the synchronous generation check at the top of
          // this callback, so `check(N) -> yield -> teardown bumps to N+1 and dispatches
          // deleteToken -> resume -> getToken() mints` is reachable. That is harmless, for
          // two independent reasons:
          //   1. a token minted after the teardown is not the one this POST carried, so the
          //      live-token comparison just below returns early and no POST is sent -- the
          //      orphan is registered nowhere;
          //   2. if `getToken()` instead wins the race and returns the token that is still
          //      live, the code falls through to forget-and-rebind, whose gate re-reads the
          //      generation, sees N+1 !== N, and sends nothing.
          // So no registration survives the teardown either way. Do not hoist the module
          // load above the generation check to restore a zero-await span: that check has to
          // stay the synchronous first statement of this callback (see its comment).
          //
          // This branch is reached only on an account mismatch, which takes a real account
          // switch, so it costs nothing on the ordinary registration path.
          let liveToken: string | null = null;
          try {
            const { FirebaseMessaging } = await loadFirebaseMessagingModule();
            liveToken = (await FirebaseMessaging.getToken()).token;
          } catch (e) {
            logger.warn('Failed to read the live FCM token before rebinding:', e);
          }
          if (liveToken !== null && liveToken !== variables.request.push_token) {
            logger.info(
              'Push registration settled for a different account on a token that is no longer live; keeping the current registration',
            );
            return;
          }
          // FALLBACK, and GATED ON `liveToken === null` so it is only ever that. When
          // `getToken()` succeeded and returned this POST's own token we have just PROVED
          // no rotation, and the stored record must not be allowed to overrule that
          // proof: on a stale `{OLD, B}` record it would keep, skip the rebind, and leave
          // the backend bound to A until the next token event, which may be days off.
          // Reachable only with no live token to compare against, where the record is the
          // only rotation evidence left. It covers only the ordering where B's
          // registration was already saved, which is why it cannot be the primary test.
          //
          // So keep a record that names a different token AND the account signed in now.
          // `stored.userId !== null` because a legacy bare-string value parses to null and
          // `liveUserId` can itself be null in this branch; an unknown account must not be
          // read as a match. Mirrors the dedupe predicate in `handleFcmToken`.
          //
          // A failed read leaves `stored` null and falls through to forget-and-rebind,
          // which is the safe direction: the dedupe fails open and the next token event
          // rebinds.
          //
          // What this does NOT do, deliberately, is compensate T1, and Copilot's remedy
          // asks for that. From the client it is not reachable, and the only POST
          // available would be actively wrong: a DELETE carries B's bearer over a token
          // the backend holds for A, and `deactivate_device_by_token` returns None on
          // exactly that mismatch, while a register-device for T1 would bind
          // the dead token to B and recreate this very leak server-side. T1's row needs
          // no client correction: no device holds T1, so the first send to it raises
          // UNREGISTERED and the backend deactivates that device itself. Cited BY SYMBOL,
          // because a line number into the other repo drifts and this stream has now had
          // six of them: backend `fcm_service.send_to_token` raises `TokenInvalidError` on
          // an UNREGISTERED error code, and `UnifiedPushService._send_fcm_to_user` catches
          // it and calls `device_crud.deactivate_device`. The row is inert until then.
          let stored: StoredPushRegistration | null = null;
          try {
            const { Preferences } = await loadPreferencesModule();
            const { value } = await Preferences.get({ key: PUSH_TOKEN_STORAGE_KEY });
            stored = parseStoredRegistration(value);
          } catch (e) {
            logger.warn('Failed to read the stored push registration before forgetting it:', e);
          }
          // Through the ref, for the same reason the rebind gate below uses one (Copilot
          // thread 4178090514): the Preferences read is an await, and an account switch
          // inside it leaves the closure naming an account that has already left. The
          // record would then be compared against the wrong account, fail the match, and
          // be forgotten and rebound -- which is the very thing this branch exists to
          // prevent, one switch later.
          const liveUserId = signedInUserIdRef.current;
          if (
            liveToken === null &&
            stored !== null &&
            stored.token !== variables.request.push_token &&
            stored.userId !== null &&
            stored.userId === liveUserId
          ) {
            logger.info(
              'Push registration settled for a different account on a rotated token; keeping the current registration',
            );
            return;
          }
          logger.info(
            'Push registration settled for a different account; forgetting it and rebinding',
          );
          try {
            const { Preferences } = await loadPreferencesModule();
            await Preferences.remove({ key: PUSH_TOKEN_STORAGE_KEY });
          } catch (e) {
            logger.warn(
              'Failed to forget a push registration that settled for another account:',
              e,
            );
          }
          // Re-read the generation after those awaits: a sign-out can begin inside them,
          // and the teardown must not be followed by a registration it cannot block.
          // `isAuthenticated` because the rebind POST needs a live session for its
          // bearer, and the paths that end one without a teardown leave the generation
          // alone. A rebind that itself settles under a further account switch lands
          // back here and compensates again, which is bounded by real switches.
          //
          // Through the REFS, not the closure -- the same rule the whole callback follows
          // (see the `signedInUserIdRef` comment: a superseded mutation's options, and so
          // its closure, are never refreshed). Here there is a second reason on top of
          // that one: every await since the callback entered is a window for another
          // account switch, so even a callback whose closure started fresh would have us
          // POST for an account that has already left. The refs carry the account signed
          // in at the moment the rebind is actually dispatched.
          const rebindUserId = signedInUserIdRef.current;
          if (
            nativePushTeardownGeneration === variables.teardownGeneration &&
            isAuthenticatedRef.current &&
            rebindUserId !== null
          ) {
            registerMutation.mutate({
              request: variables.request,
              userId: rebindUserId,
              teardownGeneration: nativePushTeardownGeneration,
            });
          }
          return;
        }
        try {
          const { Preferences } = await loadPreferencesModule();
          await Preferences.set({
            key: PUSH_TOKEN_STORAGE_KEY,
            value: serializeStoredRegistration({
              token: variables.request.push_token,
              userId: variables.userId,
            }),
          });
        } catch (e) {
          logger.warn('Failed to persist push token to Preferences:', e);
        }
      })();
    },
    onError: error => {
      logger.error('Failed to register device for push notifications:', error);
    },
  });

  // Tap listener: registered ONCE for the app's lifetime, deliberately NOT keyed
  // on `isAuthenticated`.
  //
  // Capacitor retains a cold-start `notificationActionPerformed` event until the
  // FIRST listener attaches, then drops it (Plugin.java sendRetainedArgumentsForEvent) —
  // there is no replay to a later listener. If this registration were keyed on
  // `isAuthenticated`, the flip from false to true during the dynamic import
  // would tear down run A mid-flight; run A would still attach, receive the
  // retained launch tap, discard it via its own stale `mounted` flag, and run B's
  // fresh listener would get nothing. The tap would be silently swallowed — the
  // exact bug this hook is meant to fix. Every value the handler needs is behind
  // a ref, so it has no reason to re-subscribe.
  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    let mounted = true;
    // Read through a call: the compiler narrows a plain `let` to `true` after the
    // first check and then reports every later guard as dead code, but cleanup
    // flips it during the awaits below - exactly what these guards exist for.
    const stillMounted = () => mounted;
    let cleanupActionListener: (() => Promise<void>) | null = null;

    const registerTapListener = async () => {
      const { FirebaseMessaging } = await loadFirebaseMessagingModule();

      // Bail BEFORE attaching if we were torn down during the import: attaching
      // here would consume the retained launch tap into a dead closure. Belt and
      // braces alongside the []-dep — StrictMode's dev-only double-invoke is the
      // remaining way to reach this.
      if (!stillMounted()) return;

      const listenerHandle = await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        event => {
          if (!stillMounted()) return;

          // User tapped the notification -- resolve the in-app destination.
          // AIDEV-NOTE: Capawesome emits killed-app launch taps through the same
          // retained notificationActionPerformed event on iOS and Android, which
          // is why this listener is registered before the auth-gated token setup.
          const data = event.notification.data as Record<string, unknown> | undefined;

          // De-dupe guard, ahead of every branch below: because the listener is
          // registered early, a retained launch tap and the live warm tap for the
          // same notification can both land within a tick. Running the guard first
          // keeps the N3 "honest bell" click report single-fire too. Keyed on the
          // notification identity + its deep link so two genuinely different taps
          // still both navigate.
          const deepLink =
            typeof data?.url === 'string'
              ? data.url
              : typeof data?.route === 'string'
                ? data.route
                : '';
          const tapKey = `${event.notification.id ?? 'no-id'}:${deepLink}`;
          const now = Date.now();
          const lastTap = lastHandledTapRef.current;
          if (lastTap?.key === tapKey && now - lastTap.at < TAP_DEDUPLICATION_WINDOW_MS) {
            return;
          }
          lastHandledTapRef.current = { key: tapKey, at: now };

          // N1 destination contract (task W4), dual-read transition: a payload
          // that declares a `type` resolves through resolveNotificationTarget
          // (the ONE destination authority) with its flattened scalar refs -
          // refs WIN over the legacy url/route fields when the contract resolves.
          // extractRefs returns null for type-less (pre-contract) payloads,
          // which keep the legacy ladder below verbatim until N5 stops
          // dual-emitting.
          const refs = extractRefs(data);
          if (refs) {
            // N3 "honest bell": report the tap up front (fire-and-forget) so the
            // click lands whether the destination resolves or falls through to the
            // legacy ladder. Guard the id-absent case (legacy / pre-deploy pushes).
            const notificationId = refs.notification_id;
            if (notificationId) {
              // On a cold-start tap the session is still restoring, so the
              // report would 401 and vanish (the mutation is fire-and-forget
              // with no onError). Park it for the post-auth flush instead;
              // the backend click endpoint is idempotent, so a later duplicate
              // is harmless.
              if (isAuthenticatedRef.current) {
                reportClickRef.current(notificationId);
              } else {
                pendingClickReportRef.current = notificationId;
              }
            }
            const destination = resolveNotificationTarget(refs.type, refs);
            if (destination.known) {
              navigateRef.current(destination.path);
              return;
            }
            // Resolver abstained (type not in the vendored registry, or refs
            // unbuildable). Fire the loud telemetry ONCE, then FALL THROUGH to
            // the legacy url/route ladder below: a stale-tray push from a
            // pre-contract backend pod still ships a working deep link in `url`,
            // and known=false means "resolver couldn't decide", not "discard the
            // legacy link". Only a missing/unsafe legacy link lands on home.
            logger.error(
              `Native tap destination unresolved (type=${refs.type ?? 'missing'}); falling through to legacy url`,
            );
            trackEventRef.current(ANALYTICS_EVENTS.notification_unknown_type, {
              type: refs.type ?? null,
              surface: 'native_tap',
            });
          }

          // Legacy ladder (type-less payloads AND resolver abstentions).
          // AIDEV-NOTE: 'url' is the backend's canonical deep-link field; 'route' is a
          // compat mirror added by send_to_token() in
          // app/api/services/notifications/fcm_service.py. Prefer 'url' so taps keep
          // working if that mirror is ever removed; fall back to 'route'.
          // Shared safe-path gate (same guard as web push) — rejects open redirects
          // and external schemes before handing the path to the router.
          if (isSafeInAppPath(deepLink)) {
            navigateRef.current(deepLink);
          } else {
            // A tap must always land somewhere guest-safe - unsafe/missing deep links go to
            // the shared home fallback (the notifications hub is ProtectedRoute-wrapped and
            // renders null for a signed-out / expired-session viewer).
            navigateRef.current(NOTIFICATION_TAP_FALLBACK);
          }
        },
      );

      if (!stillMounted()) {
        await listenerHandle.remove();
        return;
      }

      cleanupActionListener = () => listenerHandle.remove();
    };

    registerTapListener().catch((error: unknown) => {
      logger.error('Push notification tap listener registration failed:', error);
    });

    return () => {
      mounted = false;
      cleanupActionListener?.().catch((e: unknown) => {
        logger.warn('Push notification action listener cleanup error:', e);
      });
    };
    // Mount-once by design — see the comment above. Every value the handler
    // reads is behind a ref, so there is nothing to re-subscribe to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flush a cold-start click report once the session lands. Fires at most once
  // per parked tap: the ref is cleared before the call, so a later auth toggle
  // can't replay it.
  useEffect(() => {
    if (!isAuthenticated) return;
    const pendingId = pendingClickReportRef.current;
    if (!pendingId) return;
    pendingClickReportRef.current = null;
    reportClickRef.current(pendingId);
  }, [isAuthenticated, reportClickRef]);

  // Token registration: auth-gated, and re-runs on every auth transition.
  useEffect(() => {
    if (!isNativePlatform() || !isAuthenticated) {
      return;
    }

    let mounted = true;
    // Read through a call: the compiler narrows a plain `let` to `true` after the
    // first check and then reports every later guard as dead code, but cleanup
    // flips it during the awaits below - exactly what these guards exist for.
    const stillMounted = () => mounted;
    let cleanupTokenListener: (() => Promise<void>) | null = null;
    // See `nativePushTeardownGeneration`: a sign-out that has begun since this
    // run started means no token this run sees may be registered any more.
    const teardownGenerationAtStart = nativePushTeardownGeneration;
    // One predicate for both checkpoints. The post-await check used to re-test the
    // generation only, so a run torn down while the handler waited on the Preferences
    // read (an unmount, or `isAuthenticated` going false on the invalid-grant path with
    // no sign-out teardown) still reached `mutate()`.
    const isLive = () =>
      stillMounted() && nativePushTeardownGeneration === teardownGenerationAtStart;

    const handleFcmToken = async (fcmToken: string) => {
      // A cheap pre-await short-circuit, not a guard: it only saves a `Preferences.get`
      // on a run that is already torn down. Its coverage comes from the post-await
      // `isLive()` below, which is the pinned one, so removing this line keeps the suite
      // green -- that greenness is not a missing row.
      if (!isLive()) return;

      // Cross-session dedup: skip only if this exact token is already registered FOR
      // THIS ACCOUNT. Written in onSuccess only, so failed registrations retry, and a
      // legacy bare-string value reads as an unknown account so the backend reassigns.
      try {
        const { Preferences } = await loadPreferencesModule();
        const { value } = await Preferences.get({ key: PUSH_TOKEN_STORAGE_KEY });
        const stored = parseStoredRegistration(value);
        if (
          stored?.token === fcmToken &&
          stored.userId !== null &&
          stored.userId === signedInUserId
        ) {
          return;
        }
      } catch (e) {
        logger.warn('Failed to read push token from Preferences:', e);
      }

      // Checked again after the awaits: sign-out or an unmount can land while they run.
      if (!isLive()) return;

      logger.info('FCM token received, registering with backend');

      registerMutation.mutate({
        request: {
          platform: getNativePlatform(),
          push_token: fcmToken,
          app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
          app_variant: import.meta.env.VITE_APP_VARIANT || import.meta.env.MODE,
        },
        userId: signedInUserId,
        teardownGeneration: teardownGenerationAtStart,
      });
    };

    const setupPushNotifications = async () => {
      const { FirebaseMessaging } = await loadFirebaseMessagingModule();
      if (!stillMounted()) return;

      // Check/request permission
      let permStatus = await FirebaseMessaging.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await FirebaseMessaging.requestPermissions();
      }

      // requestPermissions() parks on a user-facing OS dialog, so teardown can
      // easily land inside that await. Re-check before registering: cleanup has
      // already run by then and would never call a remover assigned after it.
      if (!stillMounted()) return;

      if (permStatus.receive !== 'granted') {
        logger.info('Push notification permission not granted');
        return;
      }

      // Listen for FCM token updates (initial + refreshes)
      const tokenListenerHandle = await FirebaseMessaging.addListener('tokenReceived', event => {
        void handleFcmToken(event.token);
      });
      if (!stillMounted()) {
        await tokenListenerHandle.remove();
        return;
      }
      cleanupTokenListener = () => tokenListenerHandle.remove();

      // AIDEV-NOTE: Foreground notifications are handled by the native system banner
      // (configured via presentationOptions: ['badge', 'sound', 'alert'] in capacitor.config.ts).
      // No in-app toast needed — the native banner respects Focus modes and notification grouping.

      // Get the current FCM token (triggers registration if needed)
      const { token } = await FirebaseMessaging.getToken();
      await handleFcmToken(token);
    };

    setupPushNotifications().catch((error: unknown) => {
      logger.error('Push notification setup failed:', error);
    });

    return () => {
      mounted = false;
      cleanupTokenListener?.().catch((e: unknown) => {
        logger.warn('Push token listener cleanup error:', e);
      });
    };
    // registerMutation is stable (from useMutation), no need in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, signedInUserId]);
};

/**
 * How long to wait for a service-worker registration to become active before
 * treating "there is no web push subscription here" as the answer. Short on
 * purpose: by this point the registration either exists (it resolves in
 * milliseconds) or it is not coming at all. See the note at the await.
 */
const SERVICE_WORKER_READY_TIMEOUT_MS = 1000;

/**
 * Run one sign-out teardown step so that its failure is logged and nothing
 * else. Async on purpose: the step is INVOKED synchronously, before this
 * function's first await, so a caller that starts several steps in a row
 * starts them in that order, and a step that throws synchronously is caught
 * here like one that rejects.
 */
const bestEffort = async (step: () => Promise<unknown>, failure: string): Promise<void> => {
  try {
    await step();
  } catch (error) {
    // AIDEV-NOTE: sanitized: a raw AxiosError carries the bearer in `config.headers.Authorization`.
    //
    // `logger.error` is `console.error` in every build, and this is the single choke
    // point for all five teardown steps.
    logger.error(failure, sanitizeError(error));
  }
};

/**
 * End this browser's web push subscription, then ask the backend to forget it.
 * Calls the API client directly, not through React Query: this runs from
 * `executeSignOut`, outside any component, and `clearAuthState` clears the
 * query client straight after this step.
 *
 * AIDEV-NOTE: #1173. unsubscribe() starts first and the DELETE beside it; neither waits on the other.
 *
 * Why, and what pins it: "unregisterPushToken on sign-out (web)" in
 * usePushNotifications.test.tsx.
 *
 * LEGACY VAPID ONLY. This sees the root registration's VAPID subscription and nothing
 * else. Under `VITE_ONESIGNAL_PUSH_OWNER`, `useLegacyWebPushTeardown` has already
 * removed that subscription, so `getSubscription()` answers null and this returns having
 * ended nothing, while the OneSignal subscription on its own `/push/onesignal/`
 * registration stays live. Stopping delivery then rests on `OneSignal.logout()` in
 * `useOneSignalIdentity`, which races the sign-out navigation (#1223). No environment
 * sets the owner flag today (production sets no `VITE_ONESIGNAL_*`; preview sets only
 * the app id, deliberately), and the OneSignal teardown needs the backend's
 * `onesignal_send_enabled` as a second gate, so this is forward-looking.
 */
const unregisterWebPushSubscription = async (): Promise<void> => {
  try {
    if (!('serviceWorker' in navigator)) return;

    /*
     * AIDEV-NOTE [MEETBALL magic-link continuation]: `serviceWorker.ready` is
     * the one await in this module with no ceiling of its own, and it does not
     * reject -- per the spec it resolves when a registration becomes ACTIVE for
     * this scope, and in a browser that supports service workers but has none
     * (a first visit whose registration failed, a hard-reloaded scope, Safari
     * with website data cleared) it simply never settles. This is the whole of
     * the dead Log Out button traced in `useSignInOut`'s `boundedCleanup` note,
     * reached through the push step rather than the continuation clear: sign-out
     * stopped here, `logtoSignOut` was never called, and the `.finally` that
     * clears the in-flight sign-out promise never ran, so every later tap got
     * the same forever-pending promise back.
     *
     * Bounded HERE rather than left to the caller's race, so what the caller's
     * ceiling covers is the work that follows: ending the subscription and the
     * network round trip to the backend.
     */
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<undefined>(resolve => {
        setTimeout(() => {
          resolve(undefined);
        }, SERVICE_WORKER_READY_TIMEOUT_MS);
      }),
    ]);
    if (!registration) {
      logger.warn('Web push: service worker never became ready; skipping unregistration');
      return;
    }
    // AIDEV-NOTE: `registration.pushManager` is absent in an iOS Safari tab; reading through it threw.
    //
    // WebKit exposes it only where Push is enabled, which on iOS means Home Screen web
    // apps, and the app registers its service worker on every page. So in a Safari TAB
    // `ready` resolved and this accessor was undefined: a TypeError on every single
    // sign-out, caught below and written with console.error, hiding real teardown
    // failures, and it short-circuited the backend DELETE too. No subscription can exist
    // there, so there is nothing to end. Checked on the
    // registration rather than through `isWebPushSupported()`, which infers this from
    // `'PushManager' in window` and also answers false on native. The widened type is
    // the point: the DOM lib declares `pushManager` as always present, which is why
    // lint called the check dead and why nothing caught this.
    const { pushManager } = registration as Omit<ServiceWorkerRegistration, 'pushManager'> & {
      pushManager?: PushManager;
    };
    if (!pushManager) {
      logger.info('Web push: this registration exposes no pushManager; nothing to end');
      return;
    }
    const subscription = await pushManager.getSubscription();
    if (!subscription) return;

    // Everything the DELETE needs is read before the subscription is ended.
    const { endpoint } = subscription;
    const endedOnDevice = bestEffort(
      () => subscription.unsubscribe(),
      'Failed to end the web push subscription on this device:',
    );
    const forgottenByBackend = bestEffort(
      () => unsubscribeFromPush({ endpoint }, OUTLIVE_THE_SIGN_OUT_NAVIGATION),
      'Failed to unregister web push from backend:',
    );
    await Promise.all([endedOnDevice, forgottenByBackend]);
  } catch (error) {
    logger.error('Web push unregistration error during logout:', error as Error);
  }
};

/**
 * Delete this device's FCM token, then ask the backend to forget it.
 *
 * AIDEV-NOTE: #1173, the native half of the note above. deleteToken() starts FIRST and is never awaited.
 *
 * A deleted token cannot be delivered to, and the backend deactivates a device once FCM
 * reports its token UNREGISTERED: `fcm_service.send_to_token` raises `TokenInvalidError`
 * on it and `UnifiedPushService._send_fcm_to_user` deactivates the device. Cited by
 * SYMBOL, not line: the line numbers in this file's first draft had already moved by the
 * time it was reviewed.
 * The stored token is read before anything is torn down (the DELETE names the
 * device by it) and forgotten before the DELETE, so a DELETE that never
 * answers cannot leave it behind to dedupe the next account's registration.
 * The deletion does not depend on a stored token either: that is written only
 * after a successful register, and the write can fail on its own.
 *
 * Plugin and SDK behaviour worth knowing, and it differs by platform.
 *
 * iOS: the plugin observes the SDK's completion
 * (`FirebaseMessagingPlugin.swift` rejects on error), so "Failed to delete the FCM
 * token on this device" is a real signal there. `FIRMessagingTokenManager`'s
 * `deleteTokenWithAuthorizedEntity:` removes the cached token BEFORE it queues the
 * server delete and does not restore it when that fails, so an offline sign-out leaves
 * the SDK with no cached token and the backend row still active. What the next sign-in
 * then registers is server behaviour and is not decidable from the client.
 *
 * Android: `FirebaseMessagingPlugin.deleteToken` calls through and resolves at once
 * without observing the Task, and firebase-messaging deletes its stored token only
 * after the RPC succeeds, with nothing retrying a failure. So on Android a failed
 * deletion is SILENT, `getToken()` keeps handing out the same token until the RPC
 * lands, and the backend DELETE is the only half whose outcome the app can observe.
 * Observing the Task needs the plugin changed upstream.
 *
 * Neither SDK mints a replacement IN PROCESS after a delete: Android's own Javadoc says
 * "a new token will be generated the next time the app is started", and iOS posts a
 * `refresh(nil)` that the plugin drops. A replacement arrives when a run calls
 * `getToken()`, or from a refresh already scheduled or in flight (a server-initiated
 * rotation, or an Android SyncTask whose `blockingGetToken` lands after the delete) --
 * which is what `nativePushTeardownGeneration` and the sign-out latch are for. GmsCore
 * is closed source and was not inspected.
 */
const unregisterNativePushToken = async (): Promise<void> => {
  nativePushTeardownGeneration += 1;

  // FCM FIRST, and the deletion dispatched before anything touches storage. The
  // deletion needs nothing from Preferences, and a read that is slow, throws or never
  // settles used to mean it never started at all: on native the
  // `location.replace('/home')` after `logtoSignOut` then ends the document before it
  // would have run. Measured with a never-settling `Preferences.get`: 0 deleteToken, 0
  // remove and 0 DELETE calls at 200ms.
  //
  // Modules are awaited and plugins destructured from them, never the other way round:
  // a plugin that is a promise's resolution value never settles (see
  // `loadFirebaseMessagingModule`). Both loaders are the cached ones the registration
  // path uses, so neither import can fall through to the real plugin under vitest, and
  // the FCM one has already settled at mount through the tap-listener effect.
  const messagingModule = await loadFirebaseMessagingModule().catch((error: unknown) => {
    logger.error('Failed to load FCM during push token unregistration:', error as Error);
    return null;
  });
  const FirebaseMessaging = messagingModule?.FirebaseMessaging ?? null;

  // DISPATCHED, NOT AWAITED (see the `await` at the end). Nothing downstream needs the
  // deletion to have finished: a WebView navigation does not cancel a plugin call that
  // has already been dispatched, a late failure is still logged, and the 5s ceiling
  // already meant completion was never guaranteed before `logtoSignOut`. On iOS
  // `deleteToken` resolves only after the SDK's network delete, so awaiting it put that
  // round trip on the Log Out path for every native sign-out, including users who never
  // granted push permission.
  if (FirebaseMessaging) {
    void bestEffort(
      () => FirebaseMessaging.deleteToken(),
      'Failed to delete the FCM token on this device:',
    );
  }

  const preferencesModule = await loadPreferencesModule().catch((error: unknown) => {
    logger.error('Failed to load Preferences during push token unregistration:', error as Error);
    return null;
  });
  const Preferences = preferencesModule?.Preferences ?? null;
  // Its own catch, kept: without it a failed read silently skips `Preferences.remove`
  // below as well, because the whole function would reject out of this expression.
  const storedToken = Preferences
    ? await Preferences.get({ key: PUSH_TOKEN_STORAGE_KEY }).then(
        ({ value }) => parseStoredRegistration(value)?.token ?? null,
        (error: unknown) => {
          logger.error('Failed to read the push token during unregistration:', error as Error);
          return null;
        },
      )
    : null;

  // The stored copy is forgotten BEFORE the DELETE is awaited, so a DELETE that never
  // answers cannot leave it behind to dedupe the next account's registration.
  const storedTokenForgotten = Preferences
    ? bestEffort(
        () => Preferences.remove({ key: PUSH_TOKEN_STORAGE_KEY }),
        'Failed to forget the stored push token:',
      )
    : undefined;
  const forgottenByBackend = storedToken
    ? bestEffort(
        () => unregisterNativeDevice({ push_token: storedToken }),
        'Failed to unregister push token from backend:',
      )
    : undefined;
  await Promise.all([storedTokenForgotten, forgottenByBackend]);
};

/**
 * Ends this device's push registration on sign-out: the device's own
 * subscription (web) or FCM token (native) first, then the backend's record of
 * it. Never rejects. Call it while the session still exists, since the backend
 * half needs it; the device half does not.
 */
export const unregisterPushToken = async (): Promise<void> => {
  if (isNativePlatform()) {
    await unregisterNativePushToken();
  } else {
    await unregisterWebPushSubscription();
  }
};
